/**
 * Shared sign-in form — the actual inputs + submit + EmailOTP + OAuth
 * row extracted from LoginPage so the modal at SignInPromptModal can
 * reuse the same behavior (email-not-verified redirect, OTP path,
 * Google OAuth, translations) without copy-pasting and drifting.
 *
 * Consumers:
 *   - LoginPage  — wraps this in AuthLayout, hands off to nextPath on success.
 *   - SignInPromptModal — wraps this in a modal, retries the action
 *     that triggered the prompt on success.
 *
 * The form does NOT decide what happens after a successful sign-in;
 * it just calls `onSuccess()`. That's the consumer's call.
 */
import { forwardRef, useImperativeHandle, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { authClient } from '@papan/auth/client'
import { useTranslation } from '@papan/i18n'
import { PasswordInput } from '@papan/core-ui/ui'
import { OrDivider } from './AuthLayout'
import { GoogleButton } from './GoogleButton'
import { EmailOtpSignIn } from './EmailOtpSignIn'

interface Props {
  /** Called after the email/password sign-in succeeds. Consumer
   *  decides whether to navigate, retry an action, or just close. */
  onSuccess: () => void | Promise<void>
  /** Path to return to after Google OAuth. Should be absolute or
   *  start with `/`. The page-level consumer passes the post-login
   *  redirect target; the modal passes the current share-view URL. */
  googleCallbackPath: string
  /** When set, "Forgot password?" becomes a button that calls this
   *  instead of a Link to /forgot-password. AuthPromptModal uses this
   *  to swap to its forgot-password view without navigating. Pass
   *  undefined (the default) for the page-level Link behavior. */
  onForgotPassword?: () => void
}

/** Imperative handle the parent can grab to autofill the password —
 *  used by the AuthLayout dev hint + AuthPromptModal dev hint to
 *  drop the seeded `PapanDev2026!` into the password input. Email
 *  is intentionally NOT touched (matches the original AuthLayout
 *  behavior where the dev hint shows the email as a copy-able
 *  reference, only the password autofills on click). */
export interface SignInFormHandle {
  setPassword(password: string): void
}

export const SignInForm = forwardRef<SignInFormHandle, Props>(function SignInForm(
  { onSuccess, googleCallbackPath, onForgotPassword }: Props,
  ref,
) {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  // Where the person was headed, for the routes off this form (verification,
  // a forgotten password): a path — the pages there read it as `next`.
  const nextQuery = googleCallbackPath.startsWith('/') ? `?next=${encodeURIComponent(googleCallbackPath)}` : ''
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useImperativeHandle(ref, () => ({
    setPassword(p) { setPassword(p) },
  }), [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    try {
      const result = (await authClient.signIn.email({ email, password })) as unknown as {
        error?: { message?: string } | null
      }
      if (result.error) {
        const msg = result.error.message || 'Sign in failed'
        if (msg.toLowerCase().includes('verify') || msg.toLowerCase().includes('verified')) {
          navigate(`/verify-email?email=${encodeURIComponent(email)}${nextQuery.replace('?', '&')}`)
          return
        }
        setError(msg)
        return
      }
      await onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-400 bg-red-900/20 border border-red-800 rounded">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="signin-email" className="block text-sm themed-text-secondary mb-1">
            {t('login.email')}
          </label>
          <input
            id="signin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500"
            required
            autoFocus
            tabIndex={1}
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="signin-password" className="text-sm themed-text-secondary">
              {t('login.password')}
            </label>
            {onForgotPassword ? (
              <button
                type="button"
                onClick={onForgotPassword}
                tabIndex={6}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                {t('login.forgotPassword')}
              </button>
            ) : (
              <Link to={`/forgot-password${nextQuery}`} tabIndex={6} className="text-xs text-blue-400 hover:text-blue-300">
                {t('login.forgotPassword')}
              </Link>
            )}
          </div>
          <PasswordInput
            id="signin-password"
            tabIndex={2}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full themed-input border rounded-md px-3 py-2 outline-none focus:border-blue-500"
            required
          />
        </div>

        <button
          type="submit"
          tabIndex={3}
          disabled={isLoading}
          className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors"
        >
          {isLoading ? t('login.submitting') : t('login.submit')}
        </button>
      </form>

      <EmailOtpSignIn initialEmail={email} />

      <OrDivider text={t('or')} />
      <GoogleButton
        label={t('login.googleButton')}
        tabIndex={4}
        callbackURL={`${window.location.origin}${googleCallbackPath}`}
      />
    </>
  )
})
